import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import ContentSpacer from '~components/ContentSpacer';
import { DataItem } from '~components/DataItem';
import DispositionForm from '~pages/Dialler/AsteriskDiallerV2/DispositionForm';

type InfoRow = {
  label: string;
  value: string;
};

type Props = {
  name: string;
  callType: string;
  leadId: string;
  externalId: string;
  timezone: string;
  endpoint: string;
  requiredSkills: string;
  campaign: string;
  attributes: { [key: string]: string };
  endpointAttributes: { [key: string]: string };
  callbackNotes: string | null;
};

const formatLabel = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const getCallTypeColor = (callType: string): { bg: string; color: string } => {
  switch (callType.toLowerCase()) {
    case 'inbound':
      return { bg: '#ffc107', color: '#000' };
    case 'callback':
      return { bg: '#0d6efd', color: '#fff' };
    default:
      return { bg: '#009688', color: '#fff' };
  }
};

export const DetailCard = ({
  name,
  callType,
  leadId,
  externalId,
  timezone,
  endpoint,
  requiredSkills,
  campaign,
  attributes,
  endpointAttributes,
  callbackNotes,
}: Props) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const infoRows: InfoRow[] = [
    { label: 'Lead ID', value: leadId },
    { label: 'External ID', value: externalId },
    { label: 'Timezone', value: timezone },
    { label: 'Endpoint', value: endpoint },
    { label: 'Required Skills', value: requiredSkills },
    { label: 'Campaign', value: campaign },
    ...Object.entries(attributes).map(([k, v]) => ({ label: formatLabel(k), value: v })),
    ...Object.entries(endpointAttributes).map(([k, v]) => ({ label: formatLabel(k), value: v })),
    ...(callbackNotes !== null ? [{ label: 'Callback Notes', value: callbackNotes }] : []),
  ];

  return (
    <Card elevation={1} sx={{ mt: 2 }}>
      <CardHeader
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: 20,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: 1.2,
          },
        }}
        title={name}
        subheader={
          callType && (
            <Chip
              label={callType}
              size='small'
              sx={{
                marginTop: 0.5,
                paddingTop: 0,
                paddingBottom: 0,
                backgroundColor: getCallTypeColor(callType).bg,
                color: getCallTypeColor(callType).color,
                fontWeight: 500,
                borderRadius: '999px',
              }}
            />
          )
        }
      />

      <Divider />

      <CardContent sx={{ ':last-child': { paddingBottom: 2 } }}>
        <ContentSpacer spacing={2}>
          {/* Info Table */}

          {infoRows.map(({ label, value }, index) => (
            <DataItem disableMargin={index === 0} stacked={isExtraSmall} title={label} value={value} />
          ))}
        </ContentSpacer>

        <ContentSpacer spacing={2}>
          {/* Disposition */}
          <DispositionForm />
        </ContentSpacer>
      </CardContent>
    </Card>
  );
};

export default DetailCard;
